/**
 * Geo-Redirect Debug Utility
 * Comprehensive debugging for geo-redirect system issues
 * 
 * Usage: Add ?geo_debug=true to URL to enable detailed logging
 * Access debug object via window.geoDebug in console
 */

class GeoRedirectDebugger {
  constructor() {
    this.enabled = this.shouldEnableDebug();
    this.logs = [];
    this.startTime = Date.now();
    this.environment = this.detectEnvironment();
    
    if (this.enabled) {
      this.init();
    }
  }
  
  shouldEnableDebug() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('geo_debug') === 'true' || 
           window.location.hostname.includes('myshopify.com') ||
           window.location.hostname.includes('staging');
  }
  
  detectEnvironment() {
    const hostname = window.location.hostname;
    
    if (hostname.includes('myshopify.com')) return 'development';
    if (hostname.includes('staging')) return 'staging';
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) return 'local';
    return 'production';
  }
  
  init() {
    this.log('🚀 Geo-Redirect Debugger Initialized', {
      environment: this.environment,
      hostname: window.location.hostname,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
    
    this.setupGlobalErrorHandler();
    this.checkDependencies();
    this.checkScriptLoading();
    this.monitorNetworkRequests();
    this.checkThemeSettings();
    this.exposeDebugInterface();
    
    // Monitor for geo-redirect initialization
    this.waitForGeoRedirect();
  }
  
  log(message, data = null) {
    if (!this.enabled) return;
    
    const logEntry = {
      timestamp: Date.now() - this.startTime,
      message,
      data,
      stack: new Error().stack
    };
    
    this.logs.push(logEntry);
    
    // Console output with styling
    if (data) {
      console.log(`%c[GEO-DEBUG] ${message}`, 'color: #ff6b35; font-weight: bold;', data);
    } else {
      console.log(`%c[GEO-DEBUG] ${message}`, 'color: #ff6b35; font-weight: bold;');
    }
  }
  
  error(message, error = null) {
    if (!this.enabled) return;
    
    const logEntry = {
      timestamp: Date.now() - this.startTime,
      level: 'error',
      message,
      error: error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : null
    };
    
    this.logs.push(logEntry);
    console.error(`%c[GEO-DEBUG ERROR] ${message}`, 'color: #ff0000; font-weight: bold;', error);
  }
  
  setupGlobalErrorHandler() {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args.some(arg => typeof arg === 'string' && arg.includes('geo'))) {
        this.error('Console Error Detected', { args });
      }
      originalConsoleError.apply(console, args);
    };
    
    window.addEventListener('error', (event) => {
      if (event.filename && event.filename.includes('geo-redirect')) {
        this.error('Script Error', {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          message: event.message
        });
      }
    });
  }
  
  checkDependencies() {
    const dependencies = {
      'localStorage': typeof Storage !== 'undefined' && window.localStorage,
      'fetch': typeof window.fetch === 'function',
      'Promise': typeof Promise !== 'undefined',
      'JSON': typeof JSON !== 'undefined' && JSON.parse && JSON.stringify,
      'URLSearchParams': typeof URLSearchParams !== 'undefined',
      'querySelector': typeof document.querySelector === 'function'
    };
    
    const missing = Object.keys(dependencies).filter(dep => !dependencies[dep]);
    
    if (missing.length > 0) {
      this.error('Missing Dependencies', { missing, all: dependencies });
    } else {
      this.log('✅ All Dependencies Available', dependencies);
    }
  }
  
  checkScriptLoading() {
    const scripts = Array.from(document.scripts);
    const geoScripts = scripts.filter(script => 
      script.src && 
      script.src.includes('geo-redirect') && 
      !script.src.includes('geo-redirect-debug.js') // Exclude debug script from checking itself
    );
    
    this.log('Geo-Redirect Scripts Found', {
      total: geoScripts.length,
      scripts: geoScripts.map(s => ({
        src: s.src,
        loaded: s.readyState || 'unknown',
        async: s.async,
        defer: s.defer
      }))
    });
    
    // Check if scripts loaded successfully
    geoScripts.forEach(script => {
      if (script.readyState === 'complete' || script.readyState === 'loaded') {
        this.log(`✅ Script Loaded: ${script.src}`);
      } else {
        this.error(`❌ Script Not Loaded: ${script.src}`, { readyState: script.readyState });
      }
    });
  }
  
  monitorNetworkRequests() {
    // Monitor fetch requests
    const originalFetch = window.fetch;
    window.fetch = (...args) => {
      const url = args[0];
      if (typeof url === 'string' && (url.includes('ipapi.co') || url.includes('telize'))) {
        this.log('🌐 Geo API Request', { url, args });
        
        return originalFetch.apply(window, args)
          .then(response => {
            this.log('✅ Geo API Response', {
              url,
              status: response.status,
              ok: response.ok,
              headers: Object.fromEntries(response.headers.entries())
            });
            return response;
          })
          .catch(error => {
            this.error('❌ Geo API Error', { url, error });
            throw error;
          });
      }
      return originalFetch.apply(window, args);
    };
    
    // Monitor XMLHttpRequest (for legacy code)
    const originalXHR = window.XMLHttpRequest;
    window.XMLHttpRequest = function() {
      const xhr = new originalXHR();
      const originalOpen = xhr.open;
      
      xhr.open = function(method, url, ...args) {
        if (url.includes('telize') || url.includes('ipapi') || url.includes('geo')) {
          window.geoDebug?.log('🌐 XHR Geo Request', { method, url });
          
          xhr.addEventListener('load', () => {
            window.geoDebug?.log('✅ XHR Geo Response', {
              url,
              status: xhr.status,
              response: xhr.responseText?.substring(0, 200)
            });
          });
          
          xhr.addEventListener('error', () => {
            window.geoDebug?.error('❌ XHR Geo Error', { url, status: xhr.status });
          });
        }
        
        return originalOpen.apply(xhr, [method, url, ...args]);
      };
      
      return xhr;
    };
  }
  
  checkThemeSettings() {
    const settings = {
      // Check for Shopify global variables
      shopifyCountry: window.Shopify?.country,
      shopifyLocale: window.Shopify?.locale,
      shopifyCustomer: window.Shopify?.customer,
      
      // Check for theme settings
      themeSettings: window.themeSettings,
      
      // Check URL parameters
      urlParams: Object.fromEntries(new URLSearchParams(window.location.search)),
      
      // Check localStorage
      localStorage: this.getLocalStorageData(),
      
      // Check cookies
      cookies: this.getCookieData(),
      
      // Check theme editor
      isThemeEditor: this.isThemeEditor()
    };
    
    this.log('🔧 Environment Settings', settings);
  }
  
  getLocalStorageData() {
    if (!window.localStorage) return null;
    
    const geoKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.includes('geo') || key.includes('redirect') || key.includes('location')) {
        geoKeys.push({
          key,
          value: localStorage.getItem(key)
        });
      }
    }
    return geoKeys;
  }
  
  getCookieData() {
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=');
      if (key && (key.includes('geo') || key.includes('redirect') || key.includes('location'))) {
        acc[key] = value;
      }
      return acc;
    }, {});
    return cookies;
  }
  
  isThemeEditor() {
    return window.top.document.querySelector("#theme-editor") !== null ||
           window.parent !== window ||
           window.location !== window.parent.location;
  }
  
  waitForGeoRedirect() {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (window.GeoRedirectMVP) {
        this.log('✅ GeoRedirectMVP Class Found', {
          attempts,
          timeElapsed: attempts * 100
        });
        clearInterval(checkInterval);
        this.monitorGeoRedirectInstance();
        return;
      }
      
      if (window.geoRedirectMVP) {
        this.log('✅ GeoRedirectMVP Instance Found', {
          attempts,
          timeElapsed: attempts * 100,
          instance: window.geoRedirectMVP
        });
        clearInterval(checkInterval);
        this.analyzeGeoRedirectInstance(window.geoRedirectMVP);
        return;
      }
      
      if (attempts >= maxAttempts) {
        this.error('❌ GeoRedirectMVP Not Found After 5 Seconds');
        clearInterval(checkInterval);
      }
    }, 100);
  }
  
  monitorGeoRedirectInstance() {
    // Wait for instance creation
    setTimeout(() => {
      if (window.geoRedirectMVP) {
        this.analyzeGeoRedirectInstance(window.geoRedirectMVP);
      }
    }, 1000);
  }
  
  analyzeGeoRedirectInstance(instance) {
    if (!instance) return;
    
    this.log('🔍 Analyzing GeoRedirect Instance', {
      settings: instance.settings,
      userPreference: instance.userPreference,
      currentCountry: instance.currentCountry
    });
    
    // Test country detection
    this.testCountryDetection(instance);
  }
  
  async testCountryDetection(instance) {
    this.log('🧪 Testing Country Detection');
    
    try {
      const country = await instance.detectCountryWithTimeout();
      this.log('✅ Country Detection Result', { country });
    } catch (error) {
      this.error('❌ Country Detection Failed', error);
    }
  }
  
  exposeDebugInterface() {
    window.geoDebug = {
      // Expose the debugger instance
      debugger: this,
      
      // Quick access methods
      logs: () => this.logs,
      clearLogs: () => { this.logs = []; console.clear(); },
      
      // Test methods
      testGeoAPI: this.testGeoAPI.bind(this),
      testLocalStorage: this.testLocalStorage.bind(this),
      testCountryDetection: this.testCountryDetection.bind(this),
      
      // Utility methods
      getEnvironmentInfo: this.getEnvironmentInfo.bind(this),
      exportLogs: this.exportLogs.bind(this),
      
      // Reset methods
      resetPreferences: this.resetPreferences.bind(this)
    };
    
    this.log('🎛️ Debug Interface Available', {
      message: 'Access via window.geoDebug in console',
      methods: Object.keys(window.geoDebug)
    });
  }
  
  async testGeoAPI() {
    this.log('🧪 Testing Geo API Directly');
    
    const apis = [
      'https://ipapi.co/json/',
      'https://telize-v1.p.rapidapi.com/location'
    ];
    
    for (const api of apis) {
      try {
        const response = await fetch(api);
        const data = await response.json();
        this.log(`✅ ${api} Success`, data);
      } catch (error) {
        this.error(`❌ ${api} Failed`, error);
      }
    }
  }
  
  testLocalStorage() {
    this.log('🧪 Testing localStorage');
    
    const testKey = 'geo_debug_test';
    const testData = { test: true, timestamp: Date.now() };
    
    try {
      localStorage.setItem(testKey, JSON.stringify(testData));
      const retrieved = JSON.parse(localStorage.getItem(testKey));
      localStorage.removeItem(testKey);
      
      this.log('✅ localStorage Test Passed', { testData, retrieved });
    } catch (error) {
      this.error('❌ localStorage Test Failed', error);
    }
  }
  
  getEnvironmentInfo() {
    return {
      environment: this.environment,
      hostname: window.location.hostname,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      url: window.location.href,
      cookies: document.cookie,
      localStorage: this.getLocalStorageData(),
      shopify: {
        country: window.Shopify?.country,
        locale: window.Shopify?.locale,
        customer: window.Shopify?.customer
      }
    };
  }
  
  exportLogs() {
    const exportData = {
      environment: this.getEnvironmentInfo(),
      logs: this.logs,
      timestamp: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `geo-debug-${this.environment}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    this.log('📁 Debug Logs Exported');
  }
  
  resetPreferences() {
    try {
      localStorage.removeItem('geo_redirect_preference');
      this.log('✅ Geo Preferences Reset');
    } catch (error) {
      this.error('❌ Failed to Reset Preferences', error);
    }
  }
}

// Initialize debugger
if (typeof window !== 'undefined') {
  const geoDebugger = new GeoRedirectDebugger();
} 